// ============================================================
// PurchaseOrdersTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • PO list with status and receiving progress
//   • Click any row to expand its line items underneath
//   • Create form with dynamic line items
//
// A PO cannot be edited. If something is wrong, delete it and
// raise a new one — editing would rewrite the line items and
// silently destroy what has already been received against them.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. StatusBadge
//   C. LineItemsEditor  — the dynamic rows in the form
//   D. PoForm
//   E. PoDetailRow      — the expanded row
//   F. PurchaseOrdersTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, ChevronDown as Chevron } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 22px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 22px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. STATUS BADGE
// ============================================================

const STATUS_STYLE = {
  pending:   { label: 'Pending',   bg: C.infoBg,     fg: C.info },
  partial:   { label: 'Partial',   bg: C.warningBg,  fg: C.warning },
  received:  { label: 'Received',  bg: C.successBg,  fg: C.success },
  cancelled: { label: 'Cancelled', bg: C.borderSoft, fg: C.textMuted },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', background: s.bg, color: s.fg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};


// ============================================================
// C. LINE ITEMS EDITOR
//
// Lines live in an array in the parent's form state. Each row
// is edited by index, so adding and removing is just array work.
// ============================================================

const blankLine = { jumbo_id: '', quantity: '', rate: '' };

const LineItemsEditor = ({ items, setItems, jumbos }) => {
  const updateLine = (index, patch) => {
    setItems(items.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine    = () => setItems([...items, { ...blankLine }]);
  const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

  const grandTotal = items.reduce(
    (sum, l) => sum + (Number(l.quantity || 0) * Number(l.rate || 0)), 0
  );

  const cell = { padding: '8px 10px', verticalAlign: 'top' };
  const head = {
    padding: '9px 10px', fontSize: '11px', fontWeight: '600',
    color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.3px',
    background: C.rowAlt, textAlign: 'left',
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
          <thead>
            <tr>
              <th style={{ ...head, width: '44%' }}>Jumbo</th>
              <th style={{ ...head, width: '15%' }}>Qty (rolls)</th>
              <th style={{ ...head, width: '18%' }}>Rate / roll</th>
              <th style={{ ...head, width: '18%', textAlign: 'right' }}>Amount</th>
              <th style={{ ...head, width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((line, index) => {
              const amount = Number(line.quantity || 0) * Number(line.rate || 0);
              const jumbo = jumbos.find(j => String(j.id) === String(line.jumbo_id));

              return (
                <tr key={index} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                  <td style={cell}>
                    <select
                      style={inputStyle}
                      value={line.jumbo_id}
                      onChange={e => updateLine(index, { jumbo_id: e.target.value })}
                    >
                      <option value="">Select jumbo…</option>
                      {jumbos.map(j => (
                        <option key={j.id} value={j.id}>{j.name}</option>
                      ))}
                    </select>
                    {/* Spec reminder, so the buyer sees what they picked */}
                    {jumbo && (
                      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
                        {Number(jumbo.default_meters).toLocaleString()} m ·
                        {' '}{jumbo.width_inches}" ·
                        {' '}core {jumbo.core_length_meters} m
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number"
                      value={line.quantity}
                      onChange={e => updateLine(index, { quantity: e.target.value })}
                      placeholder="0"
                    />
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number" step="0.01"
                      value={line.rate}
                      onChange={e => updateLine(index, { rate: e.target.value })}
                      placeholder="0"
                    />
                  </td>
                  <td style={{ ...cell, textAlign: 'right', paddingTop: '18px', fontWeight: '600' }}>
                    {amount > 0 ? money(amount) : '—'}
                  </td>
                  <td style={{ ...cell, paddingTop: '14px' }}>
                    {/* Last remaining line can't be removed — a PO
                        with no lines is meaningless */}
                    {items.length > 1 && (
                      <IconBtn danger title="Remove line" onClick={() => removeLine(index)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        background: C.rowAlt,
      }}>
        <button
          type="button"
          onClick={addLine}
          style={{
            padding: '8px 14px', borderRadius: '7px',
            border: `1px dashed ${C.border}`, background: '#ffffff',
            color: C.textSoft, fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}
        >
          <Plus size={14} /> Add line
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: C.textSoft, fontWeight: '600' }}>Order total</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: C.text }}>
            {money(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// D. PO FORM
// ============================================================

const PoForm = ({ form, setForm, vendors, jumbos, onSave, onCancel, saving }) => (
  <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '16px', marginBottom: '20px',
    }}>
      <Field label="Vendor">
        <select
          style={inputStyle}
          value={form.vendor_id}
          onChange={e => setForm({ ...form, vendor_id: e.target.value })}
        >
          <option value="">Select vendor…</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </Field>

      <Field label="PO date">
        <TextInput
          type="date"
          value={form.po_date}
          onChange={e => setForm({ ...form, po_date: e.target.value })}
        />
      </Field>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: C.textSoft, marginBottom: '8px',
      }}>
        Line items
      </label>
      <LineItemsEditor
        items={form.items}
        setItems={items => setForm({ ...form, items })}
        jumbos={jumbos}
      />
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : 'Create order'}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </div>
);


// ============================================================
// E. DETAIL ROW
//
// Renders inside the table as an extra <tr> under the clicked
// PO. Fetches its own data the first time it opens, because the
// list query doesn't carry line items.
// ============================================================

const PoDetailRow = ({ poId, colSpan }) => {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/purchase-orders/${poId}`, { headers })
      .then(res => setPo(res.data.data))
      .catch(() => setPo(null))
      .finally(() => setLoading(false));
  }, [poId]); // eslint-disable-line

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: '#ffffff', fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: 0, background: C.rowAlt, borderTop: `1px solid ${C.borderSoft}`,
      }}>
        <div style={{ padding: '18px 22px' }}>
          {loading && <p style={{ margin: 0, color: C.textSoft, fontSize: '13px' }}>Loading…</p>}

          {!loading && po && (
            <>
              <div style={{
                border: `1px solid ${C.border}`, borderRadius: '8px',
                overflow: 'hidden', background: '#ffffff',
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...head, textAlign: 'left' }}>Jumbo</th>
                        <th style={{ ...head, textAlign: 'right' }}>Ordered</th>
                        <th style={{ ...head, textAlign: 'right' }}>Received</th>
                        <th style={{ ...head, textAlign: 'right' }}>Pending</th>
                        <th style={{ ...head, textAlign: 'right' }}>Rate</th>
                        <th style={{ ...head, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {po.items.map(item => (
                        <tr key={item.id}>
                          <td style={cell}>
                            <div style={{ fontWeight: '600' }}>{item.jumbo_name}</div>
                            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                              {Number(item.default_meters).toLocaleString()} m ·
                              {' '}{item.width_inches}" ·
                              {' '}core {item.core_length_meters} m
                            </div>
                          </td>
                          <td style={{ ...cell, textAlign: 'right' }}>{item.quantity}</td>
                          <td style={{ ...cell, textAlign: 'right', color: C.success, fontWeight: '600' }}>
                            {item.received_quantity}
                          </td>
                          <td style={{
                            ...cell, textAlign: 'right', fontWeight: '600',
                            color: item.pending_quantity > 0 ? C.warning : C.textMuted,
                          }}>
                            {item.pending_quantity > 0 ? item.pending_quantity : '—'}
                          </td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(item.rate)}</td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{money(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {po.notes && (
                <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                  <strong style={{ color: C.text }}>Notes:</strong> {po.notes}
                </div>
              )}
            </>
          )}

          {!loading && !po && (
            <p style={{ margin: 0, color: C.danger, fontSize: '13px' }}>Could not load this order.</p>
          )}
        </div>
      </td>
    </tr>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

const emptyForm = {
  vendor_id: '',
  po_date: today(),
  notes: '',
  items: [{ ...blankLine }],
};

const PurchaseOrdersTab = () => {
  const [orders, setOrders]   = useState([]);
  const [vendors, setVendors] = useState([]);
  const [jumbos, setJumbos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      const [o, v, j] = await Promise.all([
        axios.get(`${API}/tape/purchase-orders${query}`, { headers }),
        axios.get(`${API}/tape/vendors/active`, { headers }),
        axios.get(`${API}/tape/settings/jumbos/active`, { headers }),
      ]);
      setOrders(o.data.data || []);
      setVendors(v.data.data || []);
      setJumbos(j.data.data || []);
    } catch {
      notify('error', 'Could not load purchase orders. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const f = form;
    if (!f.vendor_id) return notify('error', 'Pick a vendor');
    if (!f.po_date)   return notify('error', 'PO date is required');

    const validLines = f.items.filter(
      l => l.jumbo_id && Number(l.quantity) > 0 && Number(l.rate) > 0
    );
    if (validLines.length === 0) {
      return notify('error', 'Add at least one complete line item');
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/purchase-orders`, {
        vendor_id: f.vendor_id,
        po_date: f.po_date,
        notes: f.notes,
        items: validLines,
      }, { headers });

      notify('success', 'Purchase order created');
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.po_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/tape/purchase-orders/${row.id}`, { headers });
      notify('success', 'Purchase order deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Headline numbers
  const openOrders   = orders.filter(o => ['pending', 'partial'].includes(o.status));
  const openCount    = openOrders.length;
  const openValue    = openOrders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  const pendingRolls = openOrders.reduce(
    (sum, o) => sum + (Number(o.total_ordered) - Number(o.total_received)), 0
  );

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Open orders', openCount, C.text],
          ['Rolls awaited', pendingRolls, pendingRolls > 0 ? C.warning : C.textMuted],
          ['Committed value', money(openValue), C.text],
        ].map(([label, value, color]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Purchase orders
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click any order to see its line items.
            </p>
          </div>

          <select
            style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {!form && (
            <Btn onClick={() => setForm(emptyForm)}>
              <Plus size={16} /> New order
            </Btn>
          )}
        </div>

        {form && (
          <PoForm
            form={form}
            setForm={setForm}
            vendors={vendors}
            jumbos={jumbos}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>PO number</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Lines</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Received</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && orders.map(row => {
                const ordered  = Number(row.total_ordered);
                const received = Number(row.total_received);
                const isOpen   = expandedId === row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{
                        cursor: 'pointer',
                        background: isOpen ? C.rowAlt : 'transparent',
                      }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.po_number}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.vendor_name}</td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.po_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{row.line_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{ fontWeight: '600', color: received === ordered ? C.success : C.text }}>
                          {received}
                        </span>
                        <span style={{ color: C.textMuted }}> / {ordered}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {money(row.total_amount)}
                      </td>
                      <td style={tdStyle}><StatusBadge status={row.status} /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* stopPropagation, or clicking delete would
                            also toggle the row open */}
                        {received === 0 && (
                          <IconBtn
                            danger
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && <PoDetailRow poId={row.id} colSpan={8} />}
                  </React.Fragment>
                );
              })}

              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No purchase orders yet. Click "New order" to raise the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersTab;